import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, useClerk } from "@clerk/clerk-react";
import type { User } from "@db/schema";
import { useToast } from "@/hooks/use-toast";
import { getAuthToken, clearAuthData } from "@/services/authService";

export function useUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isSignedIn, isLoaded: clerkLoaded, getToken } = useAuth();
  const { signOut } = useClerk();

  const {
    data: user,
    error,
    isLoading,
  } = useQuery<User | null>({
    queryKey: ["/api/user"],
    queryFn: async () => {
      // If Clerk says not signed in, return null immediately
      if (clerkLoaded && !isSignedIn) {
        return null;
      }

      // Try to get backend token from storage first
      let authToken = getAuthToken();

      // If no backend token but we have Clerk session, get Clerk token as fallback
      // This handles the case where the user is authenticated with Clerk but
      // the backend sync hasn't completed yet
      if (!authToken && isSignedIn) {
        try {
          authToken = await getToken();
        } catch (error) {
          console.warn("Failed to get Clerk token:", error);
        }
      }

      const headers: HeadersInit = {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      };

      // Add authorization header if we have a token
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const res = await fetch("/api/users/me", {
        credentials: "include",
        headers,
      });
      if (!res.ok) {
        if (res.status === 401) return null;
        throw new Error(await res.text());
      }
      return res.json();
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: clerkLoaded,
  });

  const updateUserMutation = useMutation({
    mutationFn: async (userData: Partial<User>) => {
      const res = await fetch("/api/users/me", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
        credentials: "include",
      });

      if (!res.ok) {
        const contentType = res.headers.get("content-type");
        let errorMessage: string;

        if (contentType && contentType.includes("application/json")) {
          const errorData = await res.json();
          errorMessage =
            errorData.message || errorData.error || "Failed to update profile";
        } else {
          // If not JSON, get the text and provide a more generic error
          const errorText = await res.text();
          console.error("Server error response:", errorText);
          errorMessage = "Server error occurred. Please try again later.";
        }

        throw new Error(errorMessage);
      }

      return res.json();
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(["/api/user"], updatedUser);
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (params: {
      userData: Partial<User>;
      headers?: HeadersInit;
    }) => {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(params.headers || {}),
        },
        body: JSON.stringify(params.userData),
        credentials: "include",
      });

      if (!res.ok) {
        const errorText = await res.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.message || errorText);
        } catch {
          throw new Error(errorText);
        }
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/user"], data);
      toast({
        title: "Success",
        description: "Logged in successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to login",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (params: {
      userData: Partial<User>;
      headers?: HeadersInit;
    }) => {
      const res = await fetch("/api/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(params.headers || {}),
        },
        body: JSON.stringify(params.userData),
        credentials: "include",
      });

      if (!res.ok) {
        const errorText = await res.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.message || errorText);
        } catch {
          throw new Error(errorText);
        }
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/user"], data);
      toast({
        title: "Success",
        description: "Registration successful",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to register",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      // Clear local auth data first
      clearAuthData();

      // Call backend logout to clear any server-side state
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch (error) {
        // Ignore errors - logout should proceed even if backend call fails
        if (process.env.NODE_ENV === "development") {
          console.warn("Backend logout error:", error);
        }
      }

      // Sign out from Clerk
      await signOut({ redirectUrl: "/" });
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/user"], null);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({
        title: "Success",
        description: "Logged out successfully",
      });
    },
    onError: (error) => {
      console.error("Logout error:", error);
      // Still clear local state even if there's an error
      clearAuthData();
      queryClient.setQueryData(["/api/user"], null);
    },
  });

  // Compute a stable loading state that accounts for Clerk loading
  // This prevents UI flash when Clerk is still initializing
  const effectiveIsLoading = !clerkLoaded || isLoading;

  return {
    user,
    isLoading: effectiveIsLoading,
    error,
    updateUser: (userData: Partial<User>) =>
      updateUserMutation.mutateAsync(userData),
    login: async (userData: Partial<User>, headers?: HeadersInit) =>
      loginMutation.mutateAsync({ userData, headers }),
    register: async (userData: Partial<User>, headers?: HeadersInit) =>
      registerMutation.mutateAsync({ userData, headers }),
    logout: logoutMutation.mutateAsync,
  };
}
